import { useEffect, useState } from 'react';
import api from '../../services/api';

// 模組層快取：同一次 App 執行只打一次 /app/version（避免每次進出「訊息」頁都重打）。
// null = 尚未查過；true/false = 查過的結果。
let cachedEnabled = null;
// [B3] 多個元件可能同時是第一個掛載的消費者（例如家長端底部導航 + 對話頁同時掛載），
// 用同一個 in-flight promise 去重，避免重複打 /app/version。
let inflightPromise = null;

function fetchEnabled() {
    if (cachedEnabled !== null) return Promise.resolve(cachedEnabled);
    if (!inflightPromise) {
        inflightPromise = api.get('/app/version')
            .then((r) => { cachedEnabled = r.data?.chat_enabled === true; return cachedEnabled; })
            .catch(() => { cachedEnabled = false; return cachedEnabled; })
            .finally(() => { inflightPromise = null; });
    }
    return inflightPromise;
}

// 讀後端 chat_enabled 旗標（app/routers/app_meta.py `/app/version`，沿用 UpdatePrompt.jsx 既有同一端點，公開端點免登入）。
// 初始值與查詢失敗都 fail-closed 為 false，確保「查不到旗標」跟「旗標關閉」是同一種安全行為：
// 維持現有「即將推出」stub 一個位元組不變；等到明確查到 true 才切換成真 UI，避免任何 loading 態閃爍。
export default function useChatEnabled() {
    const [enabled, setEnabled] = useState(cachedEnabled === true);

    useEffect(() => {
        if (cachedEnabled !== null) {
            setEnabled(cachedEnabled === true);
            return;
        }
        let alive = true;
        fetchEnabled().then((v) => { if (alive) setEnabled(v === true); });
        return () => { alive = false; };
    }, []);

    return enabled;
}

// [B3] 家長對話室透過深連結（FCM 推播）冷啟動時，可能是「頁面掛載當下旗標還沒查完」的
// 第一個消費者——若只看預設 export 的 bool，會把「還沒查完」跟「查完=false」搞混，導致
// 冷啟動時先被誤導回聯絡簿舊頁（等真正查到 true 已經太晚，因為已經 Navigate 離開了）。
// 這個 named export 額外回傳 loading，讓呼叫端可以「查完之前先顯示 loading，不要急著導頁」。
// 與預設 export 共用同一份模組層快取／同一個 in-flight promise，兩者永遠同步、不會打兩次。
export function useChatEnabledStatus() {
    const [state, setState] = useState({ enabled: cachedEnabled === true, loading: cachedEnabled === null });

    useEffect(() => {
        // 一律走 fetchEnabled()：已有快取時它回傳 Promise.resolve(cached)，效果上等同同步，
        // 但 setState 落在 .then() 這個非同步 callback 裡，不是效果本體同步執行的那一行
        // （eslint react-hooks/set-state-in-effect 只擋效果本體同步呼叫 setState 的寫法）。
        let alive = true;
        fetchEnabled().then((v) => { if (alive) setState({ enabled: v === true, loading: false }); });
        return () => { alive = false; };
    }, []);

    return state;
}
